import React, { useState, useMemo, useEffect } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  IconButton,
  Tab,
  Tabs,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tooltip,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import { DataGrid } from "@mui/x-data-grid";
import {
  Upload,
  Download,
  Refresh,
  CheckCircle,
  Error,
  AccessTime,
  Description,
  CloudUpload,
  Search,
  Close,
  FileDownload,
  Visibility,
} from "@mui/icons-material";
import DataImportWizard from "../../components/common/DataImportWizard";
import ImportTypeSelector from "../../components/import-export/ImportTypeSelector";
import { useImportHistory } from "../../hooks/queries/useImport";
import { usePermissions } from "../../hooks/usePermissions";
import PermissionGate from "../../components/common/PermissionGate";
import {
  ALL_IMPORT_PERMISSION_SLUGS,
  IMPORT_ENTITY_PERMISSION,
  canUseImportExportPermission,
} from "../../utils/importExportPermissions";
import { useQueryClient } from "@tanstack/react-query";
import importService from "../../services/importService";
import { Snackbar } from "@mui/material";

function dispositionFilename(cd) {
  if (!cd || typeof cd !== "string") return null;
  const m = /filename\*=UTF-8''([^;\n\r]+)|filename="?([^";\n\r]+)"?/i.exec(cd);
  if (!m) return null;
  try {
    return decodeURIComponent((m[1] || m[2] || "").trim());
  } catch {
    return (m[1] || m[2] || "").trim();
  }
}

function effectiveSuccessCount(item) {
  const s = Number(item?.successCount ?? item?.success);
  if (!Number.isNaN(s) && s > 0) return s;
  const t = Number(item?.totalRows);
  const f = Number(item?.failedCount ?? 0);
  if (!Number.isNaN(t) && t >= 0 && !Number.isNaN(f)) return Math.max(0, t - f);
  return 0;
}

const Import = () => {
  const { hasVisiblePermission, permissions, enabledModules } = usePermissions();
  const canImport = (slug) =>
    hasVisiblePermission(slug) || canUseImportExportPermission(enabledModules, permissions, slug);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedImportType, setSelectedImportType] = useState("teachers");
  const [importTypeSelectorOpen, setImportTypeSelectorOpen] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(10);
  const [reportPage, setReportPage] = useState(1);
  const [reportPageSize, setReportPageSize] = useState(10);
  
  // Get user info from localStorage
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const instituteId = user.instituteId || user.institute?.id;

  const { data: statsImportData = { imports: [], pagination: { total: 0 }, overallCounts: { totalRows: 0, successCount: 0, failedCount: 0 } } } = useImportHistory({ page: 1, limit: 1 });
  const statsPagination = statsImportData.pagination || { total: 0 };
  const statsOverallCounts = statsImportData.overallCounts || { totalRows: 0, successCount: 0, failedCount: 0 };

  const { data: historyImportData = { imports: [], pagination: { total: 0, page: 1, limit: 10, totalPages: 0 } }, isFetching: historyFetching, isLoading: historyLoading } = useImportHistory({ page: historyPage, limit: historyPageSize });
  const importHistory = historyImportData.imports || [];
  const importPagination = historyImportData.pagination || { total: 0, page: 1, limit: 10, totalPages: 0 };

  const { data: reportsImportData = { imports: [], pagination: { total: 0, page: 1, limit: 10, totalPages: 0 } }, isFetching: reportsFetching, isLoading: reportsLoading } = useImportHistory({ page: reportPage, limit: reportPageSize });
  const reportsPagination = reportsImportData.pagination || { total: 0, page: reportPage, limit: reportPageSize, totalPages: 0 };
  const queryClient = useQueryClient();

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setReportDialogOpen(true);
  };

  const handleDownloadImportFile = async (item) => {
    try {
      const resp = await importService.downloadImportFile(item.id);
      const blob = new Blob([resp.data], { type: resp.headers["content-type"] || "application/octet-stream" });
      const disposition = resp.headers["content-disposition"] || "";
      let fileName = item.fileName || `import_${item.id}`;
      const match = /filename\*=UTF-8''([^;\n\r]+)|filename="?([^";]+)"?/.exec(disposition);
      if (match) {
        fileName = decodeURIComponent(match[1] || match[2]);
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: `Download started: ${fileName}`, severity: 'success' });
    } catch (err) {
      console.error("Failed to download import file:", err);
      const msg = err.response?.data?.message || err.message || 'Download failed';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  const formatCellForCsv = (val) => {
    if (val == null) return "";
    if (typeof val === "object") return JSON.stringify(val);
    return String(val);
  };

  const handleDownloadReport = async (report, format = "csv") => {
    try {
      const res = await importService.downloadFullReport(report.id);
      const blob = new Blob([res.data], { type: res.headers["content-type"] || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = dispositionFilename(res.headers["content-disposition"] || res.headers["Content-Disposition"]) || `import_${report.id}_full_report.csv`;
      a.click();
      URL.revokeObjectURL(url);
      return;
    } catch (e) {}
    try {
      const res = await importService.downloadErrorReport(report.id);
      const blob = new Blob([res.data], { type: res.headers["content-type"] || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = dispositionFilename(res.headers["content-disposition"] || res.headers["Content-Disposition"]) || `import_report_${report.id}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      return;
    } catch (e) {}
    const headers = ["Row", "Field", "Value", "Error"];
    const rows = (report.errors || []).map((e) => [
      formatCellForCsv(e.row),
      formatCellForCsv(e.field),
      formatCellForCsv(e.value),
      formatCellForCsv(e.reason || e.error),
    ]);

    if (format === "csv") {
      const escapeCsv = (s) => `"${String(s).replace(/"/g, '""')}"`;
      const csvContent = [
        `Import Report: ${report.fileName}`,
        `Type: ${report.entityType}`,
        `Date: ${report.uploadedAt ? new Date(report.uploadedAt).toLocaleString() : report.uploadedAt}`,
        `Total: ${report.totalRows} | Success: ${report.successCount || 0} | Failed: ${report.failedCount || 0}`,
        "",
        headers.join(","),
        ...rows.map((r) => r.map(escapeCsv).join(",")),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `import_report_${report.id}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleDownloadSuccessReport = async (report) => {
    try {
      const res = await importService.downloadSuccessReport(report.id);
      const blob = new Blob([res.data], { type: res.headers["content-type"] || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = dispositionFilename(res.headers["content-disposition"] || res.headers["Content-Disposition"]) || `import_success_${report.entityType}_${report.id}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      const msg = e?.response?.data?.message || "No success data available for this report";
      setSnackbar({ open: true, message: msg, severity: "warning" });
    }
  };

  const handleImportClick = (templateId) => {
    setSelectedImportType(templateId);
    setUploadDialogOpen(true);
  };

  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });
  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  const reportFiltered = useMemo(() => {
    const list = reportsImportData.imports || [];
    const q = (searchQuery || "").trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (item) =>
        String(item.fileName || "").toLowerCase().includes(q) ||
        String(item.entityType || "").toLowerCase().includes(q)
    );
  }, [reportsImportData.imports, searchQuery]);

  useEffect(() => {
    setHistoryPage(1);
    setReportPage(1);
  }, [searchQuery]);

  const allTemplates = [
    // 'Institutes' import removed from admin UI — only global super-admins should perform institute creation
    {
      id: "departments",
      name: "Departments",
      description: "Import department information",
      requiredFields: "name, code",
      icon: "🏢",
    },
    {
      id: "courses",
      name: "Courses",
      description: "Import course information",
      requiredFields: "name, code, departmentName, description",
      icon: "🎓",
    },
    {
      id: "classes",
      name: "Classes",
      description: "Import class information",
      requiredFields: "name, sectionName, departmentName, courseName",
      icon: "🏫",
    },
    {
      id: "teachers",
      name: "Teachers",
      description: "Import teacher records with designation and subjects",
      requiredFields: "firstName, lastName, email, dob, phoneNumber, gender, role, qualification, experienceYears, designation, salary, departmentName, joiningDate",
      icon: "👨‍🏫",
    },
    {
      id: "subjects",
      name: "Subjects",
      description: "Import subject records with codes and descriptions",
      requiredFields: "name, code, course, semester, credits, class, teacher, description",
      icon: "📚",
    },
    {
      id: "students",
      name: "Students",
      description:
        "Import students; choose institute in the dialog. Put departmentName, className, and optional section in each CSV row (same as template).",
      requiredFields:
        "firstName, lastName, departmentName, className, email, dob, phoneNumber, gender, parentName, parentPhone, parentEmail, parentWhatsapp, admissionDate, startBatchDate, endBatchDate",
      icon: "👨‍🎓",
    },
    // {
    //   id: "superadmins",
    //   name: "Super Admins",
    //   description: "Import super administrator accounts",
    //   requiredFields: "fullName, email, phoneNumber",
    //   icon: "👑",
    // },
    {
      id: "users",
      name: "Users",
      description: "Import users. Choose user type (teacher/student/superadmin) in the dialog.",
      requiredFields: "firstName, lastName, email, role",
      icon: "👥",
    },
  ];

  const templates = useMemo(
    () =>
      allTemplates.filter((t) => {
        const perm = IMPORT_ENTITY_PERMISSION[t.id];
        return !perm || canImport(perm);
      }),
    [permissions, enabledModules, hasVisiblePermission]
  );

  const columns = [
    {
      field: "fileName",
      headerName: "File Name",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Description color="primary" />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "entityType",
      headerName: "Type",
      width: 100,
      renderCell: (params) => (
        <Tooltip title={String(params.value||"")}>
          <Chip label={params.value} size="small" />
        </Tooltip>
      ),
    },
    { field: "uploadedBy", headerName: "Uploaded By", width: 130 },
    { field: "uploadedAt", headerName: "Date & Time", width: 150 },
    { field: "totalRows", headerName: "Total", width: 80 },
    {
      field: "successCount",
      headerName: "Success",
      width: 80,
      renderCell: (params) => (
        <Typography color="success.main" fontWeight={500}>
          {params.value || 0}
        </Typography>
      ),
    },
    {
      field: "failedCount",
      headerName: "Failed",
      width: 80,
      renderCell: (params) => (
        <Typography
          color={params.value > 0 ? "error.main" : "text.secondary"}
          fontWeight={500}
        >
          {params.value || 0}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 200,
      renderCell: (params) => {
        const raw=String(params.value||"");
        const display=raw.replace(/_/g," ");
        const icons = {
          completed: <CheckCircle fontSize="small" />,
          processing: <AccessTime fontSize="small" />,
          failed: <Error fontSize="small" />,
        };
        const colors = {
          completed: "success",
          processing: "warning",
          failed: "error",
        };
        return (
          <Chip
            icon={icons[raw]}
            label={display}
            size="small"
            color={colors[raw]}
            sx={{maxWidth:"none","& .MuiChip-label":{whiteSpace:"nowrap",overflow:"visible",textOverflow:"clip"}}}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 240,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "nowrap", whiteSpace: "nowrap" }}>
          <Button size="small" startIcon={<Download />} onClick={() => handleDownloadImportFile(params.row)}>
            Download
          </Button>
          {/* <Button
            size="small"
            variant="text"
            startIcon={<FileDownload />}
            onClick={async () => {
              try {
                const resp = await importService.downloadTemplate(params.row.entityType);
                const blob = new Blob([resp.data], { type: resp.headers["content-type"] || "application/octet-stream" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `${params.row.entityType || "import"}_template.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              } catch (e) {
                const msg = e?.response?.data?.message || "Failed to download template";
                setSnackbar({ open: true, message: msg, severity: "error" });
              }
            }}
          >
            Template
          </Button> */}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Bulk Import
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Import data using Excel/CSV files
          </Typography>
        </Box>
        <PermissionGate permissions={ALL_IMPORT_PERMISSION_SLUGS}>
          <Button
            variant="contained"
            onClick={() => {
              setSelectedImportType("");
              setImportTypeSelectorOpen(true);
            }}
            startIcon={<CloudUpload />}
          >
            New Import
          </Button>
        </PermissionGate>
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
            {
              label: "Total Imports",
              value: statsPagination.total,
              icon: <Upload />,
              color: "primary.main",
            },
            {
              label: "Successful Records",
              value: statsOverallCounts.successCount || 0,
              icon: <CheckCircle />,
              color: "success.main",
            },
            {
              label: "Failed Records",
              value: statsOverallCounts.failedCount || 0,
              icon: <Error />,
              color: "error.main",
            },
          {
            label: "Success Rate",
            value: "95%",
            icon: <CheckCircle />,
            color: "info.main",
          },
        ].map((stat, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card>
              <CardContent
                sx={{ display: "flex", alignItems: "center", gap: 2 }}
              >
                <Avatar sx={{ bgcolor: stat.color, width: 48, height: 48 }}>
                  {stat.icon}
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.label}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card>
        <CardContent>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{ mb: 3 }}
          >
            <Tab label="Upload New" />
            <Tab label="Import History" />
            <Tab label="Import Reports" />
          </Tabs>

          {activeTab === 0 && (
            <Box>
              {/* Upload Area */}
              <Box
                sx={{
                  border: "2px dashed",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 4,
                  textAlign: "center",
                  mb: 3,
                  cursor: "pointer",
                  "&:hover": {
                    borderColor: "primary.main",
                    bgcolor: "action.hover",
                  },
                }}
              >
                <CloudUpload
                  sx={{ fontSize: 48, color: "primary.main", mb: 2 }}
                />
                <Typography variant="h6">Drag & drop files here</Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  or click to browse
                </Typography>
                <PermissionGate permissions={ALL_IMPORT_PERMISSION_SLUGS}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setSelectedImportType("");
                      setImportTypeSelectorOpen(true);
                    }}
                  >
                    Select Files
                  </Button>
                </PermissionGate>
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  Supported formats: .xlsx, .xls, .csv (Max 10MB)
                </Typography>
              </Box>

              {/* Templates */}
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                Available Import Types
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  overflowX: "auto",
                  pb: 2,
                  "&::-webkit-scrollbar": {
                    height: 8,
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "rgba(0,0,0,0.05)",
                    borderRadius: 4,
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "rgba(0,0,0,0.2)",
                    borderRadius: 4,
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.3)",
                    },
                  },
                }}
              >
                {templates.map((template, i) => (
                  <Card
                    key={i}
                    variant="outlined"
                    sx={{
                      minWidth: 300,
                      maxWidth: 300,
                      height: 420,
                      display: "flex",
                      flexDirection: "column",
                      "&:hover": { borderColor: "primary.main" },
                    }}
                  >
                    <CardContent sx={{ display: "flex", flexDirection: "column", height: "100%", p: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, minHeight: 48 }}>
                        <Typography variant="h4">{template.icon}</Typography>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                          {template.name}
                        </Typography>
                      </Box>
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        display="block" 
                        sx={{ 
                          mb: 2, 
                          height: 60,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {template.description}
                      </Typography>
                      <Alert severity="info" sx={{ mb: 2, minHeight: 120 }}>
                        <Typography variant="caption" fontWeight={600}>
                          Required Fields:
                        </Typography>
                        <Typography 
                          variant="caption" 
                          display="block"
                          sx={{
                            mt: 0.5,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 4,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {template.requiredFields}
                        </Typography>
                      </Alert>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<CloudUpload />}
                        onClick={() => handleImportClick(template.id)}
                        sx={{ mt: "auto" }}
                      >
                        Import {template.name}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                <Button
                  startIcon={<Refresh />}
                  onClick={() => queryClient.invalidateQueries({ queryKey: ["import", "history"] })}
                >
                  Refresh
                </Button>
              </Box>
              <DataGrid
                rows={importHistory}
                columns={columns}
                autoHeight
                disableRowSelectionOnClick
                loading={historyFetching}
                paginationMode="server"
                rowCount={importPagination.total || 0}
                paginationModel={{
                  page: Math.max(0, historyPage - 1),
                  pageSize: historyPageSize,
                }}
                onPaginationModelChange={(model) => {
                  setHistoryPage(model.page + 1);
                  setHistoryPageSize(model.pageSize);
                }}
                pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
                sx={{ border: "none" }}
              />
            </Box>
          )}

          {activeTab === 2 && (
            <Box>
              {/* Search and Filter */}
              <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                <TextField
                  placeholder="Search by filename or type..."
                  size="small"
                  sx={{ width: 300 }}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button startIcon={<Refresh />} variant="outlined" onClick={() => queryClient.invalidateQueries({ queryKey: ["import", "history"] })}>
                  Refresh
                </Button>
              </Box>

              {/* Import Reports List */}
              <Box sx={{ position: "relative" }}>
              <Backdrop sx={{ position: "absolute", zIndex: 2, borderRadius: 1, backgroundColor: "rgba(255,255,255,0.65)" }} open={reportsFetching && !reportsLoading}>
                <CircularProgress color="primary" />
              </Backdrop>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "grey.100" }}>
                      <TableCell>
                        <strong>File Name</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Type</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Date</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Total</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Success</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Failed</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Success Rate</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Status</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Actions</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportFiltered.map((report) => (
                      <TableRow
                        key={report.id}
                        sx={{ "&:hover": { bgcolor: "action.hover" } }}
                      >
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Description color="primary" fontSize="small" />
                            <Typography variant="body2">
                              {report.fileName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={report.entityType} size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {report.uploadedAt ? new Date(report.uploadedAt).toLocaleString() : report.uploadedAt}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={500}>
                            {report.totalRows}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography
                            variant="body2"
                            color="success.main"
                            fontWeight={500}
                          >
                            {report.successCount || 0}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography
                            variant="body2"
                            color={
                              (report.failedCount || 0) > 0
                                ? "error.main"
                                : "text.secondary"
                            }
                            fontWeight={500}
                          >
                            {report.failedCount || 0}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                              <LinearProgress
                              variant="determinate"
                              value={((report.successCount || 0) / (report.totalRows || 1)) * 100}
                              sx={{
                                width: 60,
                                height: 6,
                                borderRadius: 3,
                                bgcolor: "grey.200",
                                "& .MuiLinearProgress-bar": {
                                  bgcolor:
                                      (report.failedCount || 0) > 0
                                        ? "warning.main"
                                        : "success.main",
                                },
                              }}
                            />
                            <Typography variant="caption">
                              {Math.round(
                                ((report.successCount || 0) / (report.totalRows || 1)) * 100
                              )}
                              %
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={
                              report.status === "completed" ? (
                                <CheckCircle fontSize="small" />
                              ) : (
                                <AccessTime fontSize="small" />
                              )
                            }
                            label={report.status}
                            size="small"
                            color={
                              report.status === "completed"
                                ? "success"
                                : "warning"
                            }
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <Tooltip title="View Report Details">
                              <IconButton
                                size="small"
                                onClick={() => handleViewReport(report)}
                                color="primary"
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {(effectiveSuccessCount(report) > 0 || (report.failedCount || 0) > 0 || report.status === "completed" || report.status === "completed_with_errors") && (
                              <Tooltip title="Download Full Report">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDownloadReport(report)}
                                  color="success"
                                >
                                  <FileDownload fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, flexWrap: "wrap", gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Rows</InputLabel>
                  <Select label="Rows" value={reportPageSize} onChange={(e) => { setReportPageSize(Number(e.target.value)); setReportPage(1); }}>
                    {PAGINATION_PAGE_SIZE_OPTIONS.map((n) => (
                      <MenuItem key={n} value={n}>{n}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Pagination count={Math.max(1, reportsPagination.totalPages || 1)} page={reportPage} onChange={(_e, v) => setReportPage(v)} color="primary" shape="rounded" />
              </Box>

              {!reportsFetching && !reportsLoading && reportFiltered.length === 0 && (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography color="text.secondary">
                    No import reports found
                  </Typography>
                </Box>
              )}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Report Details Dialog */}
      <Dialog
        open={reportDialogOpen}
        onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setReportDialogOpen(false); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="h6">Import Report Details</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedReport?.fileName}
              </Typography>
            </Box>
            <IconButton onClick={() => setReportDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedReport && (
            <>
              {/* Summary Stats */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: "center", py: 1.5 }}>
                      <Typography variant="h5" fontWeight={700}>
                        {selectedReport.totalRows}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Rows
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={3}>
                  <Card variant="outlined" sx={{ borderColor: "success.main" }}>
                    <CardContent sx={{ textAlign: "center", py: 1.5 }}>
                        <Typography
                          variant="h5"
                          fontWeight={700}
                          color="success.main"
                        >
                        {selectedReport.successCount || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Successful
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={3}>
                  <Card variant="outlined" sx={{ borderColor: "error.main" }}>
                    <CardContent sx={{ textAlign: "center", py: 1.5 }}>
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        color="error.main"
                      >
                        {selectedReport.failedCount || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Failed
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={3}>
                  <Card variant="outlined" sx={{ borderColor: "info.main" }}>
                    <CardContent sx={{ textAlign: "center", py: 1.5 }}>
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        color="info.main"
                      >
                        {Math.round(
                          ((selectedReport.successCount || 0) / (selectedReport.totalRows || 1)) *
                            100
                        )}
                        %
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Success Rate
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Error Details */}
              {selectedReport.errors && selectedReport.errors.length > 0 ? (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      Error Details ({selectedReport.errors.length} errors)
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<FileDownload />}
                      variant="outlined"
                      color="error"
                      onClick={() => handleDownloadReport(selectedReport)}
                    >
                      Download Error Report
                    </Button>
                  </Box>

                  <Alert severity="warning" sx={{ mb: 2 }}>
                    The following records failed during import. You can download
                    the error report and fix the issues before re-importing.
                  </Alert>

                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                    {(() => {
                      // Determine if error rows are objects with the same keys
                      const rowObjects = selectedReport.errors.filter(e => e && typeof e.row === 'object');
                      const rowKeys = rowObjects.length > 0 ? Object.keys(rowObjects[0].row) : [];

                      if (rowKeys.length > 0) {
                        return (
                          <Table stickyHeader size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                                {rowKeys.map((k) => (
                                  <TableCell key={k} sx={{ fontWeight: 600 }}>{k}</TableCell>
                                ))}
                                <TableCell sx={{ fontWeight: 600 }}>Error Message</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {selectedReport.errors.slice(0, 100).map((error, i) => (
                                <TableRow key={i} sx={{ "&:hover": { bgcolor: "error.lighter" } }}>
                                  <TableCell>{i + 1}</TableCell>
                                  {rowKeys.map((k) => (
                                    <TableCell key={k}>
                                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                                        {error.row && typeof error.row === 'object' ? (error.row[k] ?? "") : ""}
                                      </Typography>
                                    </TableCell>
                                  ))}
                                  <TableCell>
                                    <Typography variant="body2" color="error.main">
                                      {error.reason || error.error}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        );
                      }

                      // Fallback: render simple list when rows aren't objects
                      return (
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Row #</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Error Message</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedReport.errors.slice(0, 100).map((error, i) => (
                              <TableRow key={i} sx={{ "&:hover": { bgcolor: "error.lighter" } }}>
                                <TableCell>{i + 1}</TableCell>
                                <TableCell>
                                  <Chip label={error.field || 'row'} size="small" variant="outlined" />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                                    {error.value || (typeof error.row === 'string' ? error.row : "(empty)")}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="error.main">{error.reason || error.error}</Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      );
                    })()}
                  </TableContainer>

                  {selectedReport.errors.length > 100 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Showing first 100 errors. Download the full report to see
                      all {selectedReport.errors.length} errors.
                    </Alert>
                  )}
                </>
              ) : (
                <Alert severity="success" icon={<CheckCircle />}>
                  All records were imported successfully! No errors found.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)}>Close</Button>
          {selectedReport?.errors?.length > 0 && (
            <Button
              variant="contained"
              color="error"
              startIcon={<FileDownload />}
              onClick={() => handleDownloadReport(selectedReport)}
            >
              Download Full Report
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Import Wizard Dialog */}
      <DataImportWizard
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        entityType={selectedImportType || undefined}
        instituteId={instituteId}
        onImportComplete={(data) => {
          setUploadDialogOpen(false);
          // Refresh import history
          queryClient.invalidateQueries({ queryKey: ["import", "history"] });
          const summary = data?.summary || data;
          const successCount = summary?.successCount ?? summary?.success ?? 0;
          const total = summary?.totalRows ?? summary?.total ?? 0;
          const failed = summary?.failedCount ?? 0;
          setSnackbar({ open: true, message: `Import completed: ${successCount} / ${total} imported${failed ? `, ${failed} failed` : ''}`, severity: failed ? 'warning' : 'success' });
        }}
      />

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Import Type Selector (New Import) */}
      <ImportTypeSelector
        open={importTypeSelectorOpen}
        onClose={() => setImportTypeSelectorOpen(false)}
        onSelect={(type) => {
          setSelectedImportType(type.key);
          setImportTypeSelectorOpen(false);
          setUploadDialogOpen(true);
        }}
      />
    </Box>
  );
};

export default Import;
